import { lazy } from 'react';
import {
  BuildingOfficeIcon,
  UsersIcon,
  CubeIcon,
  Cog6ToothIcon,
  TagIcon,
  FolderIcon,
  LinkIcon,
  RectangleStackIcon,
  ArrowUpTrayIcon,
  BellIcon,
  DocumentTextIcon,
  ShoppingCartIcon,
} from '@heroicons/react/24/outline';

const Home = lazy(() => import('@/features/common-files/pages/HomePage'));
const CommonFiles = lazy(() => import('@/features/common-files/pages/CommonFilesPage'));
const Organisations = lazy(() => import('@/features/organisations/pages/OrganisationsPage'));
const OrganisationDetail = lazy(() => import('@/features/organisations/pages/OrganisationDetailPage'));
const Users = lazy(() => import('@/features/users/pages/UsersPage'));
const UserDetail = lazy(() => import('@/features/users/pages/UserDetailPage'));

export interface RouteConfig {
  path: string;
  element: React.LazyExoticComponent<React.ComponentType>;
  auth: boolean;
  children?: RouteConfig[];
}

export const routes: RouteConfig[] = [
  {
    path: '/',
    element: Home,
    auth: true,
  },
  {
    path: '/organisations',
    element: Organisations,
    auth: true,
  },
  {
    path: '/organisations/:id/*',
    element: OrganisationDetail,
    auth: true,
  },
  {
    path: '/users',
    element: Users,
    auth: true,
  },
  {
    path: '/users/:id/*',
    element: UserDetail,
    auth: true,
  },
  {
    path: '/commonfiles',
    element: CommonFiles,
    auth: true,
  },
];

export interface SubRouteItem {
  path: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  // Which org types this route is available for (if undefined, available for all)
  orgTypes?: ('licensee' | 'licensor' | 'retailer' | 'agent')[];
}

export interface SubRouteGroup {
  groupId: string;
  groupLabel: string;
  items: SubRouteItem[];
}

export const organisationSubRouteGroups: SubRouteGroup[] = [
  {
    groupId: 'basic',
    groupLabel: 'Basic Information',
    items: [
      { path: 'company-info', label: 'Company Information', icon: BuildingOfficeIcon },
      { path: 'employees', label: 'Employees', icon: UsersIcon },
      { path: 'aliases', label: 'Aliases', icon: TagIcon },
      { path: 'config', label: 'Config', icon: Cog6ToothIcon },
      { path: 'modules', label: 'Modules', icon: CubeIcon },
    ],
  },
  {
    groupId: 'links',
    groupLabel: 'Links & Relationships',
    items: [
      { path: 'link_to_licensor', label: 'Link To Licensor', icon: LinkIcon, orgTypes: ['licensee'] },
      { path: 'link_to_licensee', label: 'Link To Licensee', icon: LinkIcon, orgTypes: ['licensor'] },
      { path: 'link_to_agent', label: 'Link to Agent', icon: LinkIcon, orgTypes: ['licensee'] },
      { path: 'licensee_link', label: 'Linked Licensees', icon: LinkIcon, orgTypes: ['agent'] },
      { path: 'brand_link', label: 'Linked Brands', icon: LinkIcon, orgTypes: ['agent'] },
      { path: 'link_tags', label: 'Link Tags', icon: TagIcon, orgTypes: ['licensee'] },
      { path: 'licensee_tags', label: 'Licensee Tags', icon: TagIcon, orgTypes: ['licensor'] },
    ],
  },
  {
    groupId: 'content',
    groupLabel: 'Content & Categories',
    items: [
      { path: 'brands', label: 'Brands', icon: RectangleStackIcon, orgTypes: ['licensor'] },
      { path: 'product_categories', label: 'Product Categories', icon: RectangleStackIcon, orgTypes: ['licensor', 'licensee'] },
      { path: 'ip_hierarchy', label: 'IP Hierarchy', icon: RectangleStackIcon, orgTypes: ['licensor'] },
      { path: 'my_files', label: 'My Files', icon: FolderIcon },
    ],
  },
  {
    groupId: 'import-export',
    groupLabel: 'Imports',
    items: [
      { path: 'export', label: 'Export/Update', icon: ArrowUpTrayIcon, orgTypes: ['licensee'] },
      { path: 'retailer_listings', label: 'Retailer Listings', icon: ShoppingCartIcon, orgTypes: ['licensee'] },
      { path: 'concepts_upload', label: 'Concepts Upload', icon: DocumentTextIcon, orgTypes: ['licensor'] },
      { path: 'contracts_upload', label: 'Contracts Upload', icon: DocumentTextIcon, orgTypes: ['licensor'] },
    ],
  },
  {
    groupId: 'notifications',
    groupLabel: 'Notifications',
    items: [
      { path: 'notifications', label: 'Notifications', icon: BellIcon },
    ],
  },
];

const DAT_PREFERRED_ORDER: string[] = [
  'company-info',
  'employees',
  'config',
  'link_to_licensor',
  'link_to_licensee',
  'link_to_agent',
  'product_categories',
  'export',
  'concepts_upload',
  'contracts_upload',
  'modules',
];

function buildDatGroups(orgType?: string): SubRouteGroup[] {
  const flatItems: SubRouteItem[] = organisationSubRouteGroups
    .flatMap((g) => g.items)
    .filter((item) => {
      if (!item.orgTypes) return true;
      return item.orgTypes.includes(orgType as 'licensee' | 'licensor' | 'retailer' | 'agent');
    });

  const indexOf = (path: string) => {
    const i = DAT_PREFERRED_ORDER.indexOf(path);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };

  const workspace = flatItems
    .filter((item) => DAT_PREFERRED_ORDER.includes(item.path))
    .sort((a, b) => indexOf(a.path) - indexOf(b.path));

  const other = flatItems.filter((item) => !DAT_PREFERRED_ORDER.includes(item.path));

  const groups: SubRouteGroup[] = [];
  if (workspace.length > 0) {
    groups.push({ groupId: 'workspace', groupLabel: 'Workspace', items: workspace });
  }
  if (other.length > 0) {
    groups.push({ groupId: 'more', groupLabel: 'More', items: other });
  }
  return groups;
}

export function getOrganisationSubRouteGroups(
  orgType?: string,
  viewProfile: 'standard' | 'dat' = 'standard',
): SubRouteGroup[] {
  if (viewProfile === 'dat') return buildDatGroups(orgType);

  return organisationSubRouteGroups
    .map((group) => ({
      ...group,
      groupLabel:
        group.groupId === 'content' && orgType === 'agent' ? 'Content' : group.groupLabel,
      items: group.items.filter((item) => {
        if (!item.orgTypes) return true;
        return item.orgTypes.includes(orgType as 'licensee' | 'licensor' | 'retailer' | 'agent');
      }),
    }))
    .filter((group) => group.items.length > 0);
}

export function getOrganisationSubRoutes(
  orgType?: string,
  viewProfile: 'standard' | 'dat' = 'standard',
): SubRouteItem[] {
  return getOrganisationSubRouteGroups(orgType, viewProfile).flatMap((group) => group.items);
}

export const userSubRoutes = [
  { path: 'user-info', label: 'User Information' },
  { path: 'user-password', label: 'Change Password' },
  { path: 'user-logins', label: 'Last Logins' },
  { path: 'linked-organisations', label: 'Linked Organisations' },
];
